/**
 * Validation Module
 * Form validation utilities
 */

import { showPhoneError } from './errors';

/**
 * Phone input element with instance reference
 */
interface PhoneInputElement extends HTMLInputElement {
    phoneInputInstance?: {
        isValidNumber(): boolean;
        getValidationError(): number;
    };
}

/**
 * Phone validation result
 */
interface PhoneValidationResult {
    valid: boolean;
    message: string;
}

/**
 * Phone error messages configuration
 */
interface PhoneMessages {
    required?: string;
    invalidCountry?: string;
    tooShort?: string;
    tooLong?: string;
    invalidNumber?: string;
    invalidLength?: string;
}

declare global {
    interface Window {
        phoneValidationMessages?: PhoneMessages;
    }
}

/**
 * Default phone error messages
 */
const DEFAULT_PHONE_MESSAGES: Required<PhoneMessages> = {
    required: 'Phone number is required',
    invalidCountry: 'Invalid country code',
    tooShort: 'Phone number is too short',
    tooLong: 'Phone number is too long',
    invalidNumber: 'Invalid phone number',
    invalidLength: 'Invalid phone number length'
};

/**
 * Sets up validation events for all form inputs.
 */
export function setupValidationEvents(form: HTMLFormElement, onValidate?: () => void): void {
    const inputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input, textarea, select'
    );

    inputs.forEach((input) => {
        const handler = (e: Event): void => {
            validateField(e.target as HTMLElement);
            if (onValidate) {
                onValidate();
            }
        };

        input.addEventListener('change', handler);
        input.addEventListener('blur', handler);
    });

    const textInputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');

    textInputs.forEach((input) => {
        input.addEventListener('keyup', (e: Event) => {
            const target = e.target as HTMLInputElement | HTMLTextAreaElement;

            if (target.value.length === 0) {
                validateField(target);
            }
            
            if (onValidate) {
                onValidate();
            }
        });
    });
}

/**
 * Validates all required inputs within a step or form container.
 * Returns false at the first invalid field found.
 */
export function validateStepInputs(selector: HTMLElement | null): boolean {
    if (!selector) {
        return true;
    }

    const requiredInputs = selector.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input:required:not([type="radio"]):not([type="checkbox"]), textarea:required, select:required'
    );

    for (const input of Array.from(requiredInputs)) {
        if (input.classList.contains('intl-tel-input')) {
            const phoneValidation = validatePhoneNumber(input as PhoneInputElement);
            if (!phoneValidation.valid) {
                return false;
            }
        } else {
            if (!input.checkValidity()) {
                return false;
            }
        }
    }

    // Validate radio button groups
    const radioGroups: Record<string, boolean> = {};
    const radioInputs = selector.querySelectorAll<HTMLInputElement>('input[type="radio"]:required');

    for (const radio of Array.from(radioInputs)) {
        const name = radio.getAttribute('name');
        if (name && !radioGroups[name]) {
            if (!selector.querySelector(`input[name="${name}"]:checked`)) {
                return false;
            }
            radioGroups[name] = true;
        }
    }

    // Validate checkbox groups
    const checkboxGroups: Record<string, boolean> = {};
    const checkboxInputs = selector.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:required');

    for (const checkbox of Array.from(checkboxInputs)) {
        const name = checkbox.getAttribute('name');
        if (name && !checkboxGroups[name]) {
            if (!selector.querySelector(`input[name="${name}"]:checked`)) {
                return false;
            }
            checkboxGroups[name] = true;
        }
    }

    return true;
}

/**
 * Validates a single field and updates its visual state (Bootstrap classes).
 */
export function validateField(elem: HTMLElement): boolean {
    const input = elem as HTMLInputElement;
    let isValid = input.checkValidity();

    if (input.classList.contains('intl-tel-input')) {
        const phoneValidation = validatePhoneNumber(input as PhoneInputElement);
        isValid = phoneValidation.valid;

        if (!isValid) {
            input.setCustomValidity(phoneValidation.message);
            showPhoneError(input, phoneValidation.message);
        } else {
            input.setCustomValidity('');
        }
    }

    input.classList.remove('is-valid', 'is-invalid');
    if (isValid) {
        input.classList.add('is-valid');
    } else {
        input.classList.add('is-invalid');
    }

    // Special handling for custom UI wrappers (like MultiSelect)
    if (input.classList.contains('custom-multiselect') && input.parentElement) {
        const wrapper = input.parentElement;
        wrapper.classList.remove('is-valid', 'is-invalid');
        
        if (isValid) {
            wrapper.classList.add('is-valid');
        } else {
            wrapper.classList.add('is-invalid');
        }
    }

    return isValid;
}

/**
 * Validates a phone number using the intl-tel-input instance.
 */
export function validatePhoneNumber(input: PhoneInputElement): PhoneValidationResult {
    const iti = input.phoneInputInstance;

    if (!iti) {
        return { valid: true, message: '' };
    }

    const phoneValue = input.value.trim();

    if (!phoneValue) {
        if (input.hasAttribute('required')) {
            return { valid: false, message: getPhoneErrorMessage('required') };
        }
        
        return { valid: true, message: '' };
    }

    if (!iti.isValidNumber()) {
        const errorCode = iti.getValidationError();
        
        return { valid: false, message: getPhoneErrorMessage(errorCode) };
    }

    return { valid: true, message: '' };
}

/**
 * Maps error codes from intl-tel-input to localized messages.
 */
export function getPhoneErrorMessage(errorCode: string | number): string {
    const messages = window.phoneValidationMessages || DEFAULT_PHONE_MESSAGES;

    const errorMessages: Record<string | number, string> = {
        'required': messages.required || DEFAULT_PHONE_MESSAGES.required,
        0: '', // Valid
        1: messages.invalidCountry || DEFAULT_PHONE_MESSAGES.invalidCountry,
        2: messages.tooShort || DEFAULT_PHONE_MESSAGES.tooShort,
        3: messages.tooLong || DEFAULT_PHONE_MESSAGES.tooLong,
        4: messages.invalidNumber || DEFAULT_PHONE_MESSAGES.invalidNumber,
        5: messages.invalidLength || DEFAULT_PHONE_MESSAGES.invalidLength,
        '-99': messages.invalidNumber || DEFAULT_PHONE_MESSAGES.invalidNumber
    };

    const result = errorMessages[errorCode];
    
    if (typeof result !== 'undefined') {
        return result;
    }

    return errorMessages['-99'];
}